import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

type FieldData = {
  x: number;
  y: number;
  size?: number;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  customText?: string; // Nouveau champ pour le texte personnalisé
  isCustomField?: boolean; // Indique si c'est un champ personnalisé
};


type CoordonnéesPDF = Record<number, Record<string, FieldData>>;

export const getPDFCoordinatesFromStorage = async (
  documentId: string = "pdfPositions",  // Nouveau paramètre avec valeur par défaut
  localStorageKey: string = "pdfCoordinatesByPage"  // Nouveau paramètre avec valeur par défaut
): Promise<CoordonnéesPDF> => {
  try {
    // 🔵 Étape 1 : Essayer Firestore en priorité
    const docRef = doc(db, "Coordinates", documentId);  // Utilisation du paramètre documentId
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data?.coordinates) {
        // 💾 Sauvegarde en localStorage (cache)
        localStorage.setItem(localStorageKey, JSON.stringify(data.coordinates));  // Utilisation du paramètre localStorageKey
        return data.coordinates as CoordonnéesPDF;
      }
    }

    // 🔶 Étape 2 : Si Firestore est vide → tenter localStorage
    const localData = localStorage.getItem(localStorageKey);  // Utilisation du paramètre localStorageKey
    if (localData) {
      console.warn(`⚠️ Firestore vide (${documentId}), chargement des coordonnées depuis localStorage`);
      
      // Migration pour les anciennes données qui n'auraient pas les nouveaux champs
      const parsedData = JSON.parse(localData) as Record<string, Record<string, any>>;
      const migratedData: CoordonnéesPDF = {};
      
      for (const [pageStr, fields] of Object.entries(parsedData)) {
        const pageNum = parseInt(pageStr);
        if (isNaN(pageNum)) continue;
        
        migratedData[pageNum] = {};
        for (const [fieldName, fieldData] of Object.entries(fields)) {
          migratedData[pageNum][fieldName] = {
            ...fieldData,
            bold: fieldData.bold ?? false,  // Valeur par défaut si non définie
            italic: fieldData.italic ?? false,  // Valeur par défaut si non définie
            customText: fieldData.customText ?? undefined,  // Valeur par défaut si non définie
            isCustomField: fieldData.isCustomField ?? false  // Valeur par défaut si non définie
          };
        }
      }
      
      return migratedData;
    }

    // 🔴 Rien trouvé
    console.warn(`⚠️ Aucune coordonnée trouvée (${documentId}) ni dans Firestore ni dans localStorage.`);
    return {};
  } catch (error) {
    console.error(`❌ Erreur lors de la récupération des coordonnées (${documentId}) depuis Firestore :`, error);

    // 🔁 Fallback localStorage en cas d'erreur Firestore
    const localData = localStorage.getItem(localStorageKey);  // Utilisation du paramètre localStorageKey
    if (localData) {
      console.warn(`⚠️ Erreur Firestore (${documentId}), récupération des coordonnées depuis localStorage.`);
      
      // Migration pour les anciennes données
      const parsedData = JSON.parse(localData) as Record<string, Record<string, any>>;
      const migratedData: CoordonnéesPDF = {};
      
      for (const [pageStr, fields] of Object.entries(parsedData)) {
        const pageNum = parseInt(pageStr);
        if (isNaN(pageNum)) continue;
        
        migratedData[pageNum] = {};
        for (const [fieldName, fieldData] of Object.entries(fields)) {
          migratedData[pageNum][fieldName] = {
            ...fieldData,
            bold: fieldData.bold ?? false,
            italic: fieldData.italic ?? false,
            customText: fieldData.customText ?? undefined,
            isCustomField: fieldData.isCustomField ?? false
          };
        }
      }
      
      return migratedData;
    }

    return {};
  }
};